import React, { useEffect, useState, useMemo, useCallback } from "react";
import { EcommProductPrice } from "@arctheme-components/ecommerce/Price/Price";
import ImageComponent from "@project-stories/elements/Image/Image";
import ProductTileQuickAction from "./ProductTileQuickAction/ProductTileQuickAction";
import ProductTileQuickAdd from "./ProductTileQuickAdd/ProductTileQuickAdd";
import ProductTileQuickAddOnHover from "./ProductTileQuickAdd/ProductTileQuickAddOnHover";
import ProductSwatches from "./ProductSwatches";
import { clsx } from "clsx";
import { getAlpineStore, mobileBreakpoint } from "../../../../entrypoints/theme";
import shopify from "../../../../js/shopify";
import useMobileBreakpoint from "@src/hooks/useMobileBreakpoint";
import { ProductTileQuickAddModal } from "./ProductTileQuickAddModal/ProductTileQuickAddModal";

// TypeScript interfaces
interface ProductImage {
  src: string;
  alt?: string;
}

interface ProductSwatch {
  url: string;
  name: string;
  image?: string;
  color?: string;
  is_product: boolean;
  show_name?: boolean;
}

interface ProductTileProps {
  product: any;
  swatches?: any[];
  siblings?: any[];
  related_products?: any[];
  show_quick_action?: boolean;
  action_type?: string;
  show_price_inline?: boolean;
  show_quick_view?: boolean;
  show_add?: boolean;
  show_quick_add?: boolean;
  swatchesPosition?: "top" | "bottom";
  showTitleColor?: boolean;
}

// Utility functions
const isSuitKit = (product: any): boolean => {
  return product?.type === 'Kit' || 
    product?.productType === 'Kit' ||
    product?.tags?.some((tag: string) => tag.includes('isSuitKit')) ||
    product?.template_suffix === 'suit-kit';
};

const getSuitComponents = (metafields: any) => [
  { key: "matching_jacket", label: "Jacket", product: metafields?.matching_jacket || metafields?.ap21?.matchingJacket?.value },
  { key: "matching_vest", label: "Waistcoat", product: metafields?.matching_vest || metafields?.ap21?.matchingVest?.value },
  { key: "matching_trouser", label: "Trousers", product: metafields?.matching_trouser || metafields?.ap21?.matchingTrouser?.value }
];

const calculatePriceFromProducts = (products: any[]): number => {
  return products.reduce((total, { product: refProduct }) => {
    if (!refProduct) return total;
    
    if (refProduct.price) {
      return total + (refProduct.price / 100);
    }
    if (refProduct.priceRange?.minVariantPrice?.amount) {
      return total + parseFloat(refProduct.priceRange.minVariantPrice.amount);
    }
    return total;
  }, 0);
};

const calculateComparePriceFromProducts = (products: any[]): number => {
  let foundComparePrice = false;
  
  const total = products.reduce((total, { product: refProduct, key, label }, index) => {
    if (!refProduct) {
      return total;
    }
    
    let componentPrice = 0;
    
    // Use compare-at price if available, otherwise use regular price
    if (refProduct.compare_at_price && refProduct.compare_at_price > 0) {
      componentPrice = refProduct.compare_at_price / 100;
      foundComparePrice = true;
    } else if (refProduct.price && refProduct.price > 0) {
      componentPrice = refProduct.price / 100;
    } else if (refProduct.priceRange?.maxVariantPrice?.amount) {
      componentPrice = parseFloat(refProduct.priceRange.maxVariantPrice.amount);
    } else if (refProduct.priceRange?.minVariantPrice?.amount) {
      componentPrice = parseFloat(refProduct.priceRange.minVariantPrice.amount);
    }
    
    return total + componentPrice;
  }, 0);
  
  // Only return the total if we found at least one compare-at price
  // Otherwise return 0 to force API fallback
  if (!foundComparePrice) {
    return 0;
  }
  
  return total;
};

const getHandlesFromProducts = (products: any[]): string[] => {
  return products
    .map(({ product: refProduct }) => refProduct?.handle)
    .filter(Boolean);
};

const createKeyValuePairs = (tags: string | string[]) => {
  const tagsArray = Array.isArray(tags) ? tags : [tags];
  return tagsArray.reduce((acc, tag) => {
    const [key, value] = tag.split(":");
    if (value) {
      if (acc[key]) {
        acc[key] = Array.isArray(acc[key]) ? [...acc[key], value] : [acc[key], value];
      } else {
        acc[key] = value;
      }
    }
    return acc;
  }, {} as Record<string, string | string[]>);
};

// Custom hooks
const useSuitPrice = (product: any) => {
  const [suitPrice, setSuitPrice] = useState<number | null>(null);

  const fetchProductPrices = useCallback(async (handles: string[], priceType: 'price' | 'compare_at_price' = 'price'): Promise<number> => {
    let totalPrice = 0;
    
    const requests = handles.map(async (handle) => {
      try {
        const response = await fetch(`/products/${handle}.js`);
        if (response.ok) {
          const data = await response.json();
          const priceValue = priceType === 'compare_at_price' 
            ? data?.variants?.[0]?.compare_at_price || 0
            : data?.variants?.[0]?.price || 0;
          return priceValue;
        }
      } catch (error) {
        // Silent error handling
      }
      return 0;
    });

    const prices = await Promise.all(requests);
    return prices.reduce((sum, price) => sum + price, 0) / 100;
  }, []);

  const calculateSuitPrice = useCallback(async (metafields: any, priceType: 'price' | 'compare_at_price' = 'price') => {
    const suitComponents = getSuitComponents(metafields);
    
    // Try to calculate from direct product references
    const directPrice = priceType === 'compare_at_price' 
      ? calculateComparePriceFromProducts(suitComponents)
      : calculatePriceFromProducts(suitComponents);
    if (directPrice > 0) {
      return directPrice;
    }

    // Fallback to API calls using handles
    const handles = getHandlesFromProducts(suitComponents);
    if (handles.length > 0) {
      return await fetchProductPrices(handles, priceType);
    }

    return 0;
  }, [fetchProductPrices]);

  useEffect(() => {
    const fetchSuitPrice = async () => {
      // Reset suit price if product is no longer a kit
      if (!isSuitKit(product)) {
        setSuitPrice(null);
        return;
      }
      
      try {
        let totalPrice = 0;

        // First try with existing metafields
        if (product.metafields) {
          totalPrice = await calculateSuitPrice(product.metafields);
        }

        // If no price found and we have a product ID, fetch full product data
        if (totalPrice === 0 && product.id) {
          const productList = await shopify.getShopifyProductList([product.id]);
          if (productList?.[0]?.metafields) {
            totalPrice = await calculateSuitPrice(productList[0].metafields);
          }
        }

        if (totalPrice > 0) {
          setSuitPrice(totalPrice);
        } else {
          // Reset to null if no valid suit price found
          setSuitPrice(null);
        }
      } catch (error) {
        setSuitPrice(null);
      }
    };

    fetchSuitPrice();
  }, [product, calculateSuitPrice]);

  return suitPrice;
};

const useSuitComparePrice = (product: any) => {
  const [suitComparePrice, setSuitComparePrice] = useState<number | null>(null);

  const fetchProductPrices = useCallback(async (handles: string[], priceType: 'price' | 'compare_at_price' = 'price'): Promise<number> => {
    const requests = handles.map(async (handle) => {
      try {
        const response = await fetch(`/products/${handle}.js`);
        if (response.ok) {
          const data = await response.json();
          
          // For suit kit compare prices, use compare-at price if available, otherwise regular price
          if (priceType === 'compare_at_price') {
            const regularPrice = data?.variants?.[0]?.price || 0;
            const comparePrice = data?.variants?.[0]?.compare_at_price || 0;
            const finalPrice = comparePrice > 0 ? comparePrice : regularPrice;
            return finalPrice;
          } else {
            const priceValue = data?.variants?.[0]?.price || 0;
            return priceValue;
          }
        }
      } catch (error) {
        // Silent error handling
      }
      return 0;
    });

    const prices = await Promise.all(requests);
    const total = prices.reduce((sum, price) => sum + price, 0) / 100;
    return total;
  }, []);

  const calculateSuitComparePrice = useCallback(async (metafields: any) => {
    const suitComponents = getSuitComponents(metafields);
    
    // Try to calculate from direct product references
    const directPrice = calculateComparePriceFromProducts(suitComponents);
    
    if (directPrice > 0) {
      return directPrice;
    }

    // Fallback to API calls using handles
    const handles = getHandlesFromProducts(suitComponents);
    
    if (handles.length > 0) {
      const apiPrice = await fetchProductPrices(handles, 'compare_at_price');
      return apiPrice;
    }

    return 0;
  }, [fetchProductPrices]);

  useEffect(() => {
    const fetchSuitComparePrice = async () => {
      // Reset suit compare price if product is no longer a kit
      if (!isSuitKit(product)) {
        setSuitComparePrice(null);
        return;
      }

      try {
        let totalComparePrice = 0;

        // First try with existing metafields
        if (product.metafields) {
          totalComparePrice = await calculateSuitComparePrice(product.metafields);
        }

        // If no price found and we have a product ID, fetch full product data
        if (totalComparePrice === 0 && product.id) {
          const productList = await shopify.getShopifyProductList([product.id]);
          if (productList?.[0]?.metafields) {
            totalComparePrice = await calculateSuitComparePrice(productList[0].metafields);
          }
        }

        if (totalComparePrice > 0) {
          setSuitComparePrice(totalComparePrice);
        } else {
          // Reset to null if no valid suit compare price found
          setSuitComparePrice(null);
        }
      } catch (error) {
        setSuitComparePrice(null);
      }
    };

    fetchSuitComparePrice();
  }, [product, calculateSuitComparePrice]);

  return suitComparePrice;
};

const useProductSwatches = (product: any, siblings: any[], relatedProducts: any[]) => {
  return useMemo(() => {
    const productTags = createKeyValuePairs(product.tags || []);
    const swatches: ProductSwatch[] = [];
    
    if (!productTags?.colour) return swatches;

    const colors = Array.isArray(productTags.colour) ? productTags.colour : [productTags.colour];
    const currentProductHandle = product.handle;

    // Process siblings
    colors.forEach((colorHandle) => {
      const siblingProduct = siblings?.find((sibling) => sibling.handle === colorHandle);
      if (siblingProduct) {
        swatches.push({
          url: `/products/${siblingProduct.handle}`,
          name: product.title,
          image: siblingProduct.images?.find((image: any) => image.src?.match(/_s_|_s(\.|$)/))?.src ||
                 siblingProduct.images?.[siblingProduct.images?.length - 1]?.src,
          is_product: siblingProduct.handle === currentProductHandle,
        });
      }
    });

    // Process related products
    colors.forEach((colorHandle) => {
      const relatedProduct = relatedProducts?.find((related) => related.handle === colorHandle);
      if (relatedProduct && !swatches.some(s => s.url.includes(relatedProduct.handle))) {
        swatches.push({
          url: `/products/${relatedProduct.handle}`,
          name: product.title,
          image: relatedProduct.images?.edges?.find((edge: any) => 
                   edge.node.src?.match(/_s_|_s(\.|$)/))?.node.src ||
                 relatedProduct.images?.edges?.[relatedProduct.images.edges.length - 1]?.node.src,
          is_product: relatedProduct.handle === currentProductHandle,
        });
      }
    });

    return swatches;
  }, [product, siblings, relatedProducts]);
};

export const ProductTile: React.FC<ProductTileProps> = ({
  product,
  swatches = [],
  siblings = [],
  related_products = [],
  show_quick_action = false,
  action_type = 'add',
  show_price_inline = false,
  show_quick_view = true,
  show_add = false,
  show_quick_add = false,
  swatches_position = "top",
  show_title_color = false
}) => {

  const store = getAlpineStore("modelview");
  const [modelView, setModelView] = useState(store.isModelView);
  const [isHovered, setIsHovered] = useState(false);
  const [relatedProducts, setRelatedProducts] = useState(related_products || []);
  const [selectedVariantImages, setSelectedVariantImages] = useState<ProductImage[]>([]);
  const [currentSwatchProduct, setCurrentSwatchProduct] = useState(null);
  const [selectedProductData, setSelectedProductData] = useState<any>(null);
  const [isLoadingSwatches, setIsLoadingSwatches] = useState(false);
  
  const suitPrice = useSuitPrice(product);
  const suitComparePrice = useSuitComparePrice(product);
  const productSwatches = useProductSwatches(product, siblings, relatedProducts);

// Handle Algolia click tracking
  const handleProductClick = useCallback(() => {
    const windowWithAlgolia = window as any;
    
    // Use current product data (selected or original)
    const trackingProduct = selectedProductData || product;
    
    // Use Algolia objectID for search results, or variant ID as fallback (since Algolia uses variant IDs)
    // Your Algolia index uses variant IDs as objectIDs
    const objectID = trackingProduct.objectID || 
                    (trackingProduct.variants?.[0]?.id?.toString()) || 
                    trackingProduct.id?.toString();
    
    if (!objectID) {
      return;
    }

    // debugging - enabled for flow tracking
    // console.log('Algolia click tracking objectID:', {
    //   objectID,
    //   source: trackingProduct.objectID ? 'algolia-search' : 
    //           trackingProduct.variants?.[0]?.id ? 'variant-fallback' : 'product-fallback',
    //   productId: trackingProduct.id,
    //   variantId: trackingProduct.variants?.[0]?.id,
    //   hasAlgoliaObjectID: !!trackingProduct.objectID,
    //   productTitle: trackingProduct.title,
    //   isSelectedVariant: !!selectedProductData
    // });

    // Try new Alpine.js component method first
    if (windowWithAlgolia.Alpine?.store('algolia')?.handleProductClick) {
      try {
        windowWithAlgolia.Alpine.store('algolia').handleProductClick({
          objectID,
          title: trackingProduct.title,
          url: `/products/${trackingProduct.handle}`,
          position: trackingProduct.position || 1,
          source: trackingProduct.objectID ? 'algolia-search' : 'shopify-direct'
        });
        return;
      } catch (error) {
        // Silent error handling
      }
    }

    // Fallback to legacy method
    if (windowWithAlgolia.algoliaProductClick) {
      try {
        // Get current queryID with proper validation
        let queryID: string | null = null;
        
        // Try multiple sources for queryID
        if (windowWithAlgolia.algoliaQueryID) {
          queryID = windowWithAlgolia.algoliaQueryID;
        } else {
          const storedQueryID = localStorage.getItem('algolia_queryID');
          const storedTimestamp = localStorage.getItem('algolia_queryID_timestamp');
          const thirtyMinutes = 30 * 60 * 1000;
          
          if (storedQueryID && storedTimestamp) {
            const age = Date.now() - parseInt(storedTimestamp);
            if (age < thirtyMinutes) {
              queryID = storedQueryID;
            }
          }
        }
          
        windowWithAlgolia.algoliaProductClick(
          objectID,
          trackingProduct.title,
          `/products/${trackingProduct.handle}`,
          queryID
        );
      } catch (error) {
        // Silent error handling
      }
    }

    // Dispatch custom event for algolia.js component
    try {
      window.dispatchEvent(new CustomEvent('algolia:productClick', {
        detail: {
          objectID,
          title: trackingProduct.title,
          url: `/products/${trackingProduct.handle}`,
          position: trackingProduct.position || 1,
          product: trackingProduct,
          source: trackingProduct.objectID ? 'algolia-search' : 'shopify-direct',
          hasAlgoliaObjectID: !!trackingProduct.objectID,
          isSelectedVariant: !!selectedProductData
        }
      }));
    } catch (error) {
      // Silent error handling
    }
  }, [product, selectedProductData]);

  useEffect(() => {
    const fetchProductSwatches = async () => {
      setIsLoadingSwatches(true);
      const relatedProductsShopify = await shopify.getRelatedProducts([product]);
      setRelatedProducts(relatedProductsShopify);
      setIsLoadingSwatches(false);
    };

    fetchProductSwatches();

    // Reset swatch states when product changes (e.g., when filters change)
    setSelectedVariantImages([]);
    setCurrentSwatchProduct(null);
    setSelectedProductData(null);

    const handleModelViewToggle = (event: CustomEvent) => {
      setModelView(event.detail.isModelView);
    };

    window.addEventListener("modelview-toggled", handleModelViewToggle as EventListener);

    // Add additional event handling to ensure swatches work
    const handleDocumentClick = (e: Event) => {
      const target = e.target as HTMLElement;
      if (target && target.closest && target.closest('[data-swatch-handle]')) {
        // The React handler should handle this
      }
    };

    document.addEventListener('click', handleDocumentClick, true);

    return () => {
      window.removeEventListener("modelview-toggled", handleModelViewToggle as EventListener);
      document.removeEventListener('click', handleDocumentClick, true);
      // Reset swatch states on unmount
      setSelectedVariantImages([]);
      setCurrentSwatchProduct(null);
      setSelectedProductData(null);
    };
  }, [product]);

  // Fetch complete product data for swatch functionality (images, price, etc.)
  const fetchRelatedProductData = useCallback(async (productHandle: string) => {
    try {
      const response = await fetch(`/products/${productHandle}.js`);
      if (response.ok) {
        const productData = await response.json();
        let images = productData.media || productData.images || [];
        
        // Filter out swatch images (images ending with _s)
        images = images.filter((image: any) => 
          image.src && !image.src.match(/_s_|_s(\.|$)/i)
        );

        // Fallback to featured image if no images
        if (!images.length && productData.featured_image) {
          images = [{ src: productData.featured_image }];
        }

        return {
          images,
          price: productData.price,
          compare_at_price: productData.compare_at_price,
          title: productData.title,
          handle: productData.handle,
          id: productData.id,
          variants: productData.variants,
          tags: productData.tags,
          type: productData.type,
          vendor: productData.vendor
        };
      }
    } catch (error) {
      // Silent error handling
    }
    return null;
  }, []);

  // Handle swatch click - update product data immediately (images, price, etc.)
  const handleSwatchClick = useCallback(async (e: React.MouseEvent, swatch: ProductSwatch) => {
    
    // Prevent all default behaviors and event bubbling
    e.preventDefault();
    e.stopPropagation();
    
    const productHandle = swatch.url.split('/').pop()?.split('?')[0];
    
    if (productHandle && productHandle !== product.handle) {
      // Update to different product variant
      setCurrentSwatchProduct(productHandle); // Set immediately for visual feedback
      
      try {
        const productData = await fetchRelatedProductData(productHandle);
        if (productData && productData.images.length > 0) {
          setSelectedVariantImages(productData.images);
          setSelectedProductData(productData);
        } else {
          setCurrentSwatchProduct(null);
          setSelectedProductData(null);
        }
      } catch (error) {
        setCurrentSwatchProduct(null);
        setSelectedProductData(null);
      }
    } else {
      // Clicking on current product swatch - reset to original
      setSelectedVariantImages([]);
      setCurrentSwatchProduct(null);
      setSelectedProductData(null);
    }
    
    return false;
  }, [product.handle, fetchRelatedProductData]);

  // Handle swatch hover - no preview, just visual feedback
  const handleSwatchHover = useCallback(() => {
    // No image preview on hover - just CSS hover effects
  }, []);

  // Handle swatch mouse leave
  const handleSwatchLeave = useCallback(() => {
    // No action needed since we removed hover preview
  }, []);

  // Process product images - use selected variant images if available
  const processedImages = useMemo(() => {
    // Use selected variant images if a swatch is clicked
    if (selectedVariantImages.length > 0) {
      return selectedVariantImages;
    }

    // Default to original product images (no hover preview)
    let images = product.media || product.images || [];
    
    // Filter out swatch images
    images = images.filter((image: any) => 
      image.src && !image.src.match(/_s_|_s(\.|$)/i)
    );

    // Fallback to featured image if no images
    if (!images.length && product.featured_image) {
      images = [{ src: product.featured_image }];
    }

    return images;
  }, [product, selectedVariantImages]);

  // Calculate image indices based on model view and swatch detection
  const { imageIndex, hoverIndex } = useMemo(() => {
    const secondImageIsSwatch = /_s$/i.test(processedImages?.[1]?.src);
    
    if (modelView) {
      if (processedImages?.length > 1 && !secondImageIsSwatch) {
        return { imageIndex: 1, hoverIndex: 0 };
      }
      return { imageIndex: 0, hoverIndex: 0 };
    }
    
    return { 
      imageIndex: 0, 
      hoverIndex: processedImages?.length > 1 ? 1 : 0 
    };
  }, [modelView, processedImages]);

  // Calculate final product price - use selected product data if available
  const currentProduct = selectedProductData || product;
  const productPrice = suitPrice !== null ? suitPrice : (currentProduct?.price / 100 || 0);
  const comparePrice = isSuitKit(product) 
    ? suitComparePrice  // For suit kits, only use suit compare price (can be null)
    : (currentProduct?.compare_at_price && currentProduct.compare_at_price > 0 
        ? currentProduct.compare_at_price / 100 
        : null); // For regular products, only use if there's a valid compare price
  const currentColor = currentProduct?.options_with_values?.find(
    (option) => option.name.toLowerCase() === "color"
  )?.values?.[0] || currentProduct?.variants?.[0]?.option1 || '';
  
  // Extract product tags and group fit - use selected product data if available
  const productTags = useMemo(() => createKeyValuePairs(currentProduct.tags || []), [currentProduct.tags]);
  const groupFit = productTags?.Group_Fit || productTags?.group_fit;

  // Get currency symbol
  const currencySymbol = (window as any).Alpine?.store('currency')?.getCurrencySymbol() || '$';

  // Get the current product URL (original or selected variant)
  const currentProductUrl = useMemo(() => {
    let url = '';
    if (currentSwatchProduct) {
      url = `/products/${currentSwatchProduct}`;
    } else {
      url = `/products/${product.handle}`;
    }
    
    if (product.objectID) {
      url += `?objectID=${product.objectID}`;
    }
    
    return url;
  }, [currentSwatchProduct, product.handle, product.objectID]);

  // Utility function for image URLs
  const getImageUrl = (image: any) => {
    return typeof image === "object" && image?.src ? image.src : image;
  };

  const isMobile = useMobileBreakpoint(mobileBreakpoint);

  return (
    <div 
      className={"theme__product-card"}
      data-product-id={product.objectID || product.id}
    >
      <div className={"productcard__content"}>
        <a
          href={currentProductUrl}
          className="relative w-full productcard__content__image"
          onMouseEnter={() => {
            setIsHovered(true);
          }}
          onMouseLeave={() => setIsHovered(false)}
          onClick={handleProductClick}
        >
          <ImageComponent
            classes={clsx(
              "product-image transition-opacity duration-300",
              isHovered &&
                processedImages.length > 1 &&
                "opacity-0",
            )}
            imageUrl={getImageUrl(processedImages[imageIndex])}
          />
          <ImageComponent
            classes={clsx(
              "product-image absolute top-0 left-0 transition-opacity duration-300",
              isHovered && processedImages.length > 1
                ? "opacity-100"
                : "opacity-0",
            )}
            imageUrl={getImageUrl(processedImages[hoverIndex])}
          />
          
          {/* Quick Add Component */}
          {show_add && (
            <ProductTileQuickAdd product={product} />
          )}

          {show_quick_add && !isSuitKit(currentProduct || product) && !productTags.preorder && (
            <ProductTileQuickAddOnHover  product={currentProduct || product} isHovered={isHovered} />
          )}

          {/* Quick Action Component (Quick Add or Quick View) */}
          {show_quick_action && !isMobile && isHovered && !isSuitKit(currentProduct || product) && !productTags.preorder && (
            <ProductTileQuickAction
              product={currentProduct || product}
              type={action_type}
            />
          )}
        </a>

        {swatches_position === "top" && (
          <>  
            {isLoadingSwatches && (
              <div className="h-[26px] w-20 mt-4 skeleton"></div>
            )}
             <ProductSwatches
              productSwatches={productSwatches}
              currentSwatchProduct={currentSwatchProduct}
              handleSwatchClick={handleSwatchClick}
              handleSwatchHover={handleSwatchHover}
              handleSwatchLeave={handleSwatchLeave}
              className="mt-[16px]"
            />
          </>
        )}
        

        <div className={"product-info flex justify-between mt-[16px] flex-col lg:flex-row"}>
          <div>
            <a href={currentProductUrl} onClick={handleProductClick}>
              <p className={"b2 text-[#54565A]"}>{currentProduct?.title} {show_title_color && `- ${currentColor}`}</p>
            </a>
            <p className={"bc uppercase text-[13px] font-[500] text-[#54565A]"}>
              {groupFit}
            </p>
          </div>

          <div className="flex items-center justify-between">
            <div
              className={
                "product-info-price flex items-center justify-end flex-row-reverse lg:flex-col lg:items-end"
              }
            >
              {!show_price_inline && (
                <span className={`b1 font-regular text-[#54565a] ${comparePrice && comparePrice > 0 && productPrice < comparePrice ? 'price-current--sale' : ''}`}>
                  <EcommProductPrice
                    price={`${currencySymbol}${productPrice.toFixed(2)}`}
                    compare_at_price={comparePrice}
                  />
                </span>
              )}
              {comparePrice && comparePrice > 0 && productPrice < comparePrice && (
                <span className="price-compare-at text-[12px] line-through text-[#BBBBBD] mr-[8px] lg:mr-0">
                  <div className="product__price" data-prodify-price-container="">
                    {`${currencySymbol}${comparePrice.toFixed(2)}`}
                  </div>
                </span>
              )}
            </div>

            {show_quick_action && isMobile && !isSuitKit(currentProduct || product) && !productTags.preorder && (
              <ProductTileQuickAction
                product={currentProduct || product}
                type={action_type}
              />
            )}
          </div>
        </div>

        {swatches_position === "bottom" && (
          <>  
            {isLoadingSwatches && (
              <div className="h-[26px] w-20 mt-4 skeleton"></div>
            )}
            <ProductSwatches
              productSwatches={productSwatches}
              currentSwatchProduct={currentSwatchProduct}
              handleSwatchClick={handleSwatchClick}
              handleSwatchHover={handleSwatchHover}
              handleSwatchLeave={handleSwatchLeave}
              className="mt-[16px]"
            />
          </>
        )}

        <div className="mt-[16px] empty:hidden">
          {productTags.automatic && (
            <p className="b3 text-[14px] font-[400] text-[#CDA176]">
              {productTags.automatic}
            </p>
          )}
          {productTags.preorder && (
            <p className="bg-[#54565a] text-white px-3 py-1 text-xs font-semibold inline-block uppercase">
              PRE-ORDER
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductTile;

function setCurrentSwatchColor(colorNameConcat: any) {
  throw new Error("Function not implemented.");
}
